import React, { useCallback, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import Alert from '@material-ui/lab/Alert';
import { useWindowWidth } from '@react-hook/window-size';

import GreenButton from '../../common/styled/GreenButton/GreenButton';
import CustomLoader from '../../components/CustomLoader/CustomLoader';
import CertificateDetailsDialog from '../../dialogs/CertificateDetailsDialog/CertificateDetailsDialog';
import { ReduxState } from '../../redux/types';
import Certificate from '../../types/Certificate';
import Company, { EmptyCompany } from '../../types/Company';
import api from '../../utils/api';
import CertificateCard from './components/CertificateCard/CertificateCard';
import { FlexContainer, PlaceholderCard, StyledGridList, Title } from './styled';

const CertificatesPage = () => {
    const { companyId } = useParams<{ companyId: string }>();

    const userInfo = useSelector((state: ReduxState) => state.user);
    const isAdmin = userInfo.type === 'admin';
    const realCompanyId = companyId || userInfo.companyId;

    const { t } = useTranslation();

    const windowWidth = useWindowWidth();
    const titleSuffix = windowWidth < 900 ? '_small' : '';

    const [isLoading, setIsLoading] = useState(true);
    const [isDetailsDialogOpen, setIsDetailsDialogOpen] = useState(false);

    const [companyData, setCompanyData] = useState<Company>(EmptyCompany);
    const [certificates, setCertificates] = useState<Certificate[]>([]);
    const [selectedCertificateId, selectCertificateId] = useState(0);

    const getCertificates = useCallback(() => {
        api.request('/companies/' + realCompanyId + '/certificates').then((res) => {
            setCertificates(res);
            setIsLoading(false);
        });
    }, [realCompanyId]);

    const getData = useCallback(() => {
        api.request('/companies/' + realCompanyId).then((companyRes) => {
            setCompanyData(companyRes);
            getCertificates();
        });
    }, [getCertificates, realCompanyId]);

    const openDetailsDialog = useCallback((certificateId: number) => {
        selectCertificateId(certificateId);
        setIsDetailsDialogOpen(true);
    }, []);

    const closeDetailsDialog = useCallback(() => {
        setIsDetailsDialogOpen(false);
    }, []);

    useEffect(() => {
        getData();
    }, [getData]);

    if (isLoading) {
        return <CustomLoader />;
    }

    return (
        <>
            <FlexContainer>
                <Title variant='h4'>
                    {companyId ? (
                        <Trans i18nKey={'pages.certificates.title_with_company' + titleSuffix} tOptions={{ company: companyData.name }}>
                            _<small>{companyData.name}</small>_
                        </Trans>
                    ) : t('pages.certificates.title' + titleSuffix)}
                </Title>

                {(isAdmin || userInfo.type === 'certifier') && (
                    <GreenButton variant='outlined' onClick={() => openDetailsDialog(0)} >
                        {t('pages.certificates.new_certificate')}
                    </GreenButton>
                )}
            </FlexContainer>

            <Alert style={{ maxWidth: '1200px', margin: '12px auto 0' }} severity='info'>
                {t('pages.certificates.description' + titleSuffix, { company: companyData.name })}
            </Alert>

            <StyledGridList>
                {certificates.map(certificate => {
                    return (
                        <CertificateCard
                            certificate={certificate}
                            openEditDialog={() => openDetailsDialog(certificate.id)}
                            updateData={getCertificates}
                            key={certificate.id}
                            hasActiveAssessment={companyData?.hasActiveAssessment ?? false}
                        />
                    );
                })}

                {
                    // displays some placeholder cards to don't break the grid layout
                    [...Array(Math.ceil(windowWidth / 300)).keys()].map(item => {
                        return (
                            <PlaceholderCard key={'placeholder_' + item} />
                        );
                    })
                }
            </StyledGridList>

            <CertificateDetailsDialog
                open={isDetailsDialogOpen}
                onClose={closeDetailsDialog}
                companyId={realCompanyId}
                certificateId={selectedCertificateId}
                updateData={getCertificates}
            />
        </>
    );
};

export default CertificatesPage;
